<template>
  <div>
    <n-search-container :fields="searchFields" @search="search" @reset="reset">
      <n-search-input ref="search" v-model="params" :fields="searchFields" />
    </n-search-container>

    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t('selection.noOption') }}
              </template>
            </v-select>
          </b-col>
          <b-col class="text-right">
            <b-button variant="primary" @click="openCalendar">
              {{ $t('button.add') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :type="permissionType"
          :route="route"
          :updatable="false"
          @sort-changed="sortChanged"
          @destroy="destroy"
        >
          <template #cell(title)="row">
            {{  getDisplayTitle(row.item.title) }}
          </template>
          <template #cell(isActive)="row">
            <b-badge
              :variant="row.item.isActive ? 'success' : 'danger'"
              class="mr-1"
            >
              {{
                row.item.isActive
                  ? $t('label.active')
                  : $t('label.inActive')
              }}
            </b-badge>
          </template>
          <template #cell(type)="row">
            {{ $t(`workingSchedule.types.${row.item.type}`) }}
          </template>
          <template #cell(createdAt)="row">
            {{ row.item.createdAt | formatDate('LL HH:mm') }}
          </template>
          <template #front-button="row">
            <b-button
              v-b-tooltip.hover
              :title="$t('general.update')"
              variant="primary"
              pill
              size="sm"
              @click="editItem(row.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>

    <!-- Calendar Modal -->
    <calendar-modal
      v-if="showCalendar"
      :data="selectedItem"
      @success="onSuccess"
      @hidden="showCalendar = false"
    />
  </div>
</template>

<script>
  import { BCard, BButton, BRow, BCol, BBadge } from 'bootstrap-vue';
  import vSelect from 'vue-select';
  import Repository from '@/repositories/RepositoryFactory';
  import NPagination from '@/components/NPagination.vue';
  import NTable from '@/components/NTable.vue';
  import NSearchContainer from '@/components/NSearchContainer.vue';
  import NSearchInput from '@/components/NSearchInput.vue';
  import tableField from './tableField';
  import SearchInputs from './searchInput';
  import CalendarModal from './CalendarModal.vue';

  const WorkingScheduleRepository = Repository.get('workingScheduleTemplate');

  export default {
    name: 'WorkingScheduleListV2',
    components: {
      BCard,
      BButton,
      BRow,
      BCol,
      BBadge,
      vSelect,
      CalendarModal,
      NPagination,
      NTable,
      NSearchContainer,
      NSearchInput
    },
    data() {
      return {
        showCalendar: false,
        s: null,
        items: [],
        loading: false,
        total: 0,
        perPageOptions: this.$store.state.pagination.perPageOptions,
        params: {
          title: this.$route.query.title || null,
          type: this.$route.query.type || null
        },
        query: {
          page: Number(this.$route.query.page) || 1,
          order: this.$route.query.order || null,
          sort: this.$route.query.sort || null,
          title: this.$route.query.title || null,
          type: this.$route.query.type || null
        },
        selectedItem: null
      };
    },
    computed: {
      perPage: {
        get() {
          return this.$store.state.pagination.perPage;
        },
        set(value) {
          this.$store.commit('pagination/SET_PER_PAGE', value);
        }
      },
      locale() {
        return this.$i18n.locale.split('-')[0];
      }
    },
    watch: {
      perPage() {
        this.list(1);
      }
    },
    mounted() {
      this.getData();
    },
    methods: {
      getLocalizedTitle(title) {
        if (title && typeof title === 'object') {
          return title[this.locale] || title.en || title.zh || title.km || title.vi || '';
        }
        return title || '';
      },
      getDisplayTitle(title) {
        return this.getLocalizedTitle(title);
      },
      openCalendar() {
        this.showCalendar = true;
        this.selectedItem = null;
      },

      updateQueryString() {
        this.$router.replace({ query: this.query });
      },

      search() {
        this.query = {
          ...this.query,
          ...this.params
        };
        this.updateQueryString();
        this.getData();
      },

      reset() {
        this.$refs.search.reset();
        this.query = {
          ...this.query,
          ...this.params
        };
        this.updateQueryString();
        this.getData();
      },

      list(page = 1) {
        this.query.page = page;
        this.updateQueryString();
        this.getData();
      },

      sortChanged(sortBy, sortDirection) {
        this.query.order = sortBy;
        this.query.sort = sortDirection;
        this.updateQueryString();
        this.getData();
      },

      getData() {
        this.loading = true;
        WorkingScheduleRepository.list({
          ...this.query,
          departmentId: this.$route.params.departmentId
        })
          .then(response => {
            if (response?.data?.data) {
              const { list, total } = response.data.data;
              this.items = list || [];
              this.total = total || 0;
            }
          })
          .catch(error => {
            console.error('Error fetching data:', error);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      async editItem(item) {
        try {
          const response = await WorkingScheduleRepository.show(item.id);

          this.selectedItem = response.data.data;
          this.showCalendar = true;
        } catch (error) {
          console.error('Error fetching events:', error);
        }
      },

      destroy(id) {
        this.loading = true;
        WorkingScheduleRepository.destroy(id)
          .then(() => {
            this.getData();
          })
          .catch(error => {
            console.error('Error deleting item:', error);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      onSuccess() {
        this.showCalendar = false;
        this.getData();
      }
    },
    setup() {
      const permissionType = 'manage-users';
      const route = 'working-schedule';
      const fields = tableField;
      const searchFields = SearchInputs;
      return {
        permissionType,
        route,
        fields,
        searchFields
      };
    }
  };
</script>

<style lang="scss" scoped></style>
